import type { FilterDTO, FilterItemDTO } from "../../search";

export abstract class TransmissionTransformer {
  /**
   * Transforms a `FilterDTO` for transmission types by grouping all automatic transmissions under
   * a single "Automático" label with a combined count. "Manual" transmissions are kept as a separate item.
   *
   * @param {FilterDTO} filter - The filter object to transform.
   * @returns {FilterDTO} - The transformed filter object with items categorized as "Automático" and "Manual".
   */
  static transform(filter: FilterDTO): FilterDTO {
    if (!filter || filter.name !== "Transmission") return filter;

    const manualItem = filter.items.find((item) => item.name === "Manual");

    const automaticItems = filter.items.filter(
      (item) => item.name !== "Manual",
    );

    const automaticCount = automaticItems.reduce(
      (sum, item) => sum + item.count,
      0,
    );

    const automaticTransmissions: FilterItemDTO | null =
      automaticCount > 0
        ? {
            label: "Automático",
            name: automaticItems.map((item) => item.name).join("|"),
            count: automaticCount,
            items: [],
          }
        : null;

    const transformedItems: FilterItemDTO[] = [
      ...(manualItem ? [manualItem] : []),
      ...(automaticTransmissions ? [automaticTransmissions] : []),
    ].sort((a, b) => {
      const labelA = a.label ?? "";
      const labelB = b.label ?? "";
      return labelA.localeCompare(labelB);
    });

    return {
      ...filter,
      items: transformedItems,
    };
  }

  /**
   * Transforms an array of transmission types from URL parameters by mapping all automatic
   * transmission types under a single "Automático" label. Each "Manual" transmission is preserved separately.
   *
   * @param {string[]} gears - The list of transmission types from URL parameters.
   * @returns {Array<{ value: string, label: string }>} - An array of objects with `value` and `label` for each transmission type.
   */
  static transformFromUrl(
    gears: string[],
  ): Array<{ value: string; label: string }> {
    const transformedGears: { value: string; label: string }[] = [];
    const automaticTransmissions: string[] = [];
    const mappedAutomaticTransmissions =
      "Automática|Automática Sequencial|Automatizada|Automatizada DCT|CVT|Semi-automática";

    for (const gear of gears) {
      if (gear === "Manual") {
        transformedGears.push({ value: gear, label: gear });
      } else if (mappedAutomaticTransmissions.includes(gear)) {
        automaticTransmissions.push(gear);
      }
    }

    if (automaticTransmissions.length > 0) {
      transformedGears.push({
        value: automaticTransmissions.join("|"),
        label: "Automático",
      });
    }

    return transformedGears;
  }
}
